import type { PrimaryFilterId } from '../../filters/types/filters';
import type { ChartType } from '../PageConfigProvider/types/chart';
import type { SecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import { DataProvider } from './DataProvider';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
  type DataProviderSet,
} from './types';

export type MultiMetricDataMap = Partial<
  Record<MetricValues, DataProviderSet | undefined>
>;

export type MultiMetricProviderResult = {
  byMetric: MultiMetricDataMap;
  fallback?: DataProviderSet;
};

type MultiMetricProviderProps = {
  primaryDimension?: PrimaryFilterId;
  metrics: MetricValues[];
  chartType: ChartType;
  metricChartTypes?: Partial<Record<MetricValues, ChartType>>;
  secondaryDimension?: SecondaryDimensionValues;
  children: DataProviderChildren<MultiMetricProviderResult>;
};

const combineStatuses = (
  headStatus: DataProviderQueryStatus,
  tailStatus: DataProviderQueryStatus
): DataProviderQueryStatus => {
  if (
    headStatus === DataProviderQueryStatus.Error ||
    tailStatus === DataProviderQueryStatus.Error
  ) {
    return DataProviderQueryStatus.Error;
  }
  if (
    headStatus === DataProviderQueryStatus.Fetching ||
    tailStatus === DataProviderQueryStatus.Fetching
  ) {
    return DataProviderQueryStatus.Fetching;
  }
  if (
    headStatus === DataProviderQueryStatus.Success &&
    tailStatus === DataProviderQueryStatus.Success
  ) {
    return DataProviderQueryStatus.Success;
  }
  return DataProviderQueryStatus.Idle;
};

export const MultiMetricProvider = ({
  primaryDimension,
  metrics,
  chartType,
  metricChartTypes,
  secondaryDimension,
  children,
}: MultiMetricProviderProps) => {
  if (metrics.length === 0) {
    return children({
      data: { byMetric: {}, fallback: undefined },
      status: DataProviderQueryStatus.Success,
    });
  }

  return (
    <CombineProviders
      primaryDimension={primaryDimension}
      metrics={metrics}
      chartType={chartType}
      metricChartTypes={metricChartTypes}
      secondaryDimension={secondaryDimension}
    >
      {children}
    </CombineProviders>
  );
};

const CombineProviders = ({
  primaryDimension,
  metrics,
  chartType,
  metricChartTypes,
  secondaryDimension,
  children,
}: {
  primaryDimension?: PrimaryFilterId;
  metrics: MetricValues[];
  chartType: ChartType;
  metricChartTypes?: Partial<Record<MetricValues, ChartType>>;
  secondaryDimension?: SecondaryDimensionValues;
  children: DataProviderChildren<MultiMetricProviderResult>;
}) => {
  const [headMetric, ...restMetrics] = metrics;

  if (headMetric === undefined) {
    return children({
      data: { byMetric: {} },
      status: DataProviderQueryStatus.Success,
    });
  }

  const effectiveChartType = metricChartTypes?.[headMetric] ?? chartType;

  if (restMetrics.length === 0) {
    return (
      <DataProvider
        primaryDimension={primaryDimension}
        chartType={effectiveChartType}
        metric={headMetric}
        secondaryDimension={secondaryDimension}
        suppressDownload
        doSideEffects
      >
        {({ data, status }) => {
          return children({
            data: { byMetric: { [headMetric]: data } },
            status,
          });
        }}
      </DataProvider>
    );
  }

  return (
    <DataProvider
      primaryDimension={primaryDimension}
      chartType={effectiveChartType}
      metric={headMetric}
      secondaryDimension={secondaryDimension}
      suppressDownload
      doSideEffects
    >
      {({ data: headData, status: headStatus }) => (
        <CombineProviders
          primaryDimension={primaryDimension}
          metrics={restMetrics}
          chartType={chartType}
          metricChartTypes={metricChartTypes}
          secondaryDimension={secondaryDimension}
        >
          {({ data: tailData, status: tailStatus }) => {
            return children({
              data: {
                byMetric: {
                  [headMetric]: headData,
                  ...(tailData?.byMetric || {}),
                },
              },
              status: combineStatuses(headStatus, tailStatus),
            });
          }}
        </CombineProviders>
      )}
    </DataProvider>
  );
};
